import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  CpuChipIcon,
  DocumentTextIcon,
  ChartBarIcon,
  CloudArrowUpIcon,
  CheckCircleIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

const FreeGSTSoftware: React.FC = () => {
  const features = [
    {
      icon: CpuChipIcon,
      title: '99.5% Accurate AI Invoice Scanner',
      description: 'Extract GSTIN, HSN codes, invoice amounts automatically from any invoice format - PDF, images, or scanned documents',
    },
    {
      icon: DocumentTextIcon,
      title: 'Free GSTR-1 & GSTR-3B Generation',
      description: 'Automatically generate GST returns from processed invoices. Export in Excel/JSON formats for direct upload',
    },
    {
      icon: CloudArrowUpIcon,
      title: 'Client GST Upload Portal',
      description: 'Give clients a branded portal to upload their GST bills and invoices directly to you',
    },
    {
      icon: ChartBarIcon,
      title: 'GST Analytics Dashboard',
      description: 'Real-time GST liability tracking, input credit analysis, and compliance reports for all clients',
    },
  ];

  const benefits = [
    'Free forever plan for up to 100 invoices/month',
    'No credit card required to start',
    'Process GST invoices 10x faster than manual entry',
    'Eliminate 99% of data entry errors in GST filing',
    'Scale your CA practice without hiring GST staff',
    'Bank-grade security with full audit trail',
  ];

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'AICaFriend - Free GST Software',
    applicationCategory: 'BusinessApplication',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'INR',
      description: 'Free forever plan for up to 100 invoices per month',
    },
    description: 'Free AI-powered GST invoice processing software for chartered accountants in India. Automate GST return filing, invoice scanning, and client management.',
    operatingSystem: 'Web-based',
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      ratingCount: '500',
    },
  };

  return (
    <>
      <SEO
        title="Free GST Software for CA Firms - AI-Powered Invoice Processing"
        description="Free AI-powered GST software for chartered accountants. Automate GST invoice scanning, GSTR-1/3B generation, and client management. No credit card required. Trusted by 500+ CA firms."
        keywords="free gst software, gst software for ca, free gst filing software, ai gst invoice scanner, gstr-1 software, chartered accountant gst tool, free gst automation"
        canonicalUrl="https://aicafriend.com/free-gst-software"
        structuredData={structuredData}
      />

      <div className="bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-green-50 via-white to-blue-50 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
            <div className="text-center">
              <div className="inline-flex items-center bg-green-100 text-green-800 px-4 py-2 rounded-full mb-6">
                <SparklesIcon className="h-5 w-5 mr-2" />
                <span className="font-semibold">100% Free Forever Plan Available</span>
              </div>

              <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
                Free GST Software for
                <span className="gradient-text"> CA Firms in India</span>
              </h1>

              <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-4xl mx-auto">
                Free AI-powered GST invoice processing and return filing software for chartered accountants.
                Automate GSTR-1, GSTR-3B generation from scanned invoices with 99.5% accuracy.
                No credit card required to start.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Link
                  to="/pricing"
                  className="btn-primary px-8 py-4 text-lg font-semibold rounded-lg text-white shadow-lg"
                >
                  Start Free Trial - No CC Required
                </Link>
                <Link
                  to="/features"
                  className="btn-secondary px-8 py-4 text-lg font-semibold rounded-lg"
                >
                  See How It Works
                </Link>
              </div>

              <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-600">
                <div className="flex items-center">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                  Free for 100 invoices/month
                </div>
                <div className="flex items-center">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                  500+ CA firms trust us
                </div>
                <div className="flex items-center">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                  99.5% accuracy
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Complete Free GST Software Suite for CA Firms
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Everything you need to automate GST invoice processing and return filing - completely free
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="card-hover bg-white p-8 rounded-xl border border-gray-200"
                >
                  <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-blue-500 rounded-lg flex items-center justify-center mb-4">
                    <feature.icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Why Choose Our Free GST Software?
                </h2>
                <p className="text-xl text-gray-600 mb-8">
                  Trusted by 500+ chartered accountants across India to automate their GST practice
                </p>
                <div className="space-y-4">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircleIcon className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
                      <span className="text-gray-700 text-lg">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-r from-green-500 to-blue-600 rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-6">Free Forever Plan Includes:</h3>
                <ul className="space-y-4">
                  <li className="flex items-center">
                    <CheckCircleIcon className="h-6 w-6 mr-3" />
                    <span>100 invoices per month</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircleIcon className="h-6 w-6 mr-3" />
                    <span>AI invoice scanning & extraction</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircleIcon className="h-6 w-6 mr-3" />
                    <span>GSTR-1 & GSTR-3B generation</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircleIcon className="h-6 w-6 mr-3" />
                    <span>Client upload portal</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircleIcon className="h-6 w-6 mr-3" />
                    <span>GST analytics dashboard</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircleIcon className="h-6 w-6 mr-3" />
                    <span>Email support</span>
                  </li>
                </ul>

                <Link
                  to="/pricing"
                  className="mt-8 block w-full bg-white text-green-600 px-6 py-3 text-center rounded-lg font-semibold hover:bg-gray-50 transition-all duration-200"
                >
                  Start Free Now
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                How Our Free GST Software Works
              </h2>
              <p className="text-xl text-gray-600">
                Get started in 3 simple steps
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  1
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Sign Up Free</h3>
                <p className="text-gray-600">
                  Create your free account in under 2 minutes. No credit card required.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  2
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Upload GST Invoices</h3>
                <p className="text-gray-600">
                  Upload PDF/image invoices or let clients upload directly via their portal.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  3
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Generate GST Returns</h3>
                <p className="text-gray-600">
                  AI extracts data automatically. Download GSTR-1/3B ready to file.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-green-600 to-blue-600">
          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Start Using Free GST Software Today
            </h2>
            <p className="text-xl text-green-100 mb-8">
              Join 500+ CA firms automating their GST practice with AICaFriend. Free forever for up to 100 invoices/month.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/pricing"
                className="bg-white text-green-600 px-8 py-4 text-lg font-semibold rounded-lg hover:bg-gray-50 transform hover:scale-105 transition-all duration-200"
              >
                Get Started Free
              </Link>
              <Link
                to="/contact"
                className="border-2 border-white text-white px-8 py-4 text-lg font-semibold rounded-lg hover:bg-white hover:text-green-600 transition-all duration-200"
              >
                Schedule Demo
              </Link>
            </div>
            <p className="mt-6 text-sm text-green-100">
              No credit card required • Free forever plan • Upgrade anytime
            </p>
          </div>
        </section>

        {/* Internal Links Section for SEO */}
        <section className="py-12 bg-gray-50 border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Explore More GST Solutions</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Link
                to="/ai-invoice-processing"
                className="p-6 bg-white rounded-lg border border-gray-200 hover:shadow-lg transition-all duration-200"
              >
                <h4 className="font-semibold text-gray-900 mb-2">AI Invoice Processing</h4>
                <p className="text-gray-600 text-sm">Learn how our AI extracts GST data from invoices automatically</p>
              </Link>
              <Link
                to="/gst-automation-for-ca"
                className="p-6 bg-white rounded-lg border border-gray-200 hover:shadow-lg transition-all duration-200"
              >
                <h4 className="font-semibold text-gray-900 mb-2">GST Automation for CAs</h4>
                <p className="text-gray-600 text-sm">Discover how to automate your entire GST workflow</p>
              </Link>
              <Link
                to="/case-studies"
                className="p-6 bg-white rounded-lg border border-gray-200 hover:shadow-lg transition-all duration-200"
              >
                <h4 className="font-semibold text-gray-900 mb-2">CA Firm Case Studies</h4>
                <p className="text-gray-600 text-sm">See how other CA firms save 80% time on GST work</p>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default FreeGSTSoftware;
